import { useState, useEffect, useMemo } from "react";
import { getDistinctWorkflowIds } from "~/pages/dashboard/task/tab-windows/utils/window-helpers";
import type { Item, List, Status } from "~/types/api";
import { useWindowDataLoader } from "../../../../tab-windows/hooks/apis/use-window-data-loader";
import { useTabWindowsContext } from "../../../../tab-windows/hooks/use-tab-windows-context";
import type { StatusGroup } from "../../../../task.types";

type ListsDataState = {
  lists: List[];
  statuses: Status[];
  isLoading: boolean;
};

type Props = {
  sortedItems: StatusGroup[];
  windowId: string;
};

type ReturnProps = ListsDataState & {
  allItems: Item[];
  appliedListIds: number[];
  hasActiveFilter: boolean;
};

export const useListsData = ({ sortedItems, windowId }: Props): ReturnProps => {
  const [lists, setLists] = useState<List[]>([]);
  const [statuses, setStatuses] = useState<Status[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const { getWindow } = useTabWindowsContext();
  const { fetchListForWorkflow } = useWindowDataLoader();

  const currentWindow = getWindow(windowId);
  const appliedListIds = currentWindow?.state.appliedListIds || [];
  const hasActiveFilter = appliedListIds.length > 0;

  const allItems: Item[] = useMemo(() => {
    if (currentWindow?.originalItems) {
      return (currentWindow.originalItems as StatusGroup[]).flatMap((group) => group.children);
    }
    return sortedItems.flatMap((group) => group.children);
  }, [currentWindow?.originalItems, sortedItems]);

  const distinctWorkflowIds = useMemo(() => {
    const itemsToCheck = (currentWindow?.originalItems || sortedItems) as StatusGroup[];
    return getDistinctWorkflowIds(itemsToCheck);
  }, [currentWindow?.originalItems, sortedItems]);

  const workflowIdsKey = useMemo(() => distinctWorkflowIds.join(","), [distinctWorkflowIds]);

  useEffect(() => {
    const itemsToExtractFrom = (currentWindow?.originalItems || sortedItems) as StatusGroup[];
    const statusMap = new Map<number, Status>();
    itemsToExtractFrom.forEach((group) => {
      statusMap.set(group.id, { code: group.id, name: group.name } as Status);
    });
    setStatuses(Array.from(statusMap.values()));
  }, [currentWindow?.originalItems, sortedItems]);

  useEffect(() => {
    if (distinctWorkflowIds.length === 0) {
      setIsLoading(false);
      return;
    }

    setIsLoading(true);
    fetchListForWorkflow(distinctWorkflowIds)
      .then((fetchedLists) => {
        setLists(fetchedLists);
        setIsLoading(false);
      })
      .catch((error) => {
        console.error("Failed to fetch lists:", error);
        setIsLoading(false);
      });
  }, [workflowIdsKey, fetchListForWorkflow]);

  return {
    lists,
    statuses,
    isLoading,
    allItems,
    appliedListIds,
    hasActiveFilter,
  };
};
